// v2.19.25 — Carte de Couverture Nationale CI (OPS).
// Choropleth Mapbox sur ~474 sous-préfectures (OSM+GADM), illumination par
// adresses revendiquées. KPI + tooltip + drill-down + export PNG/GIF/MP4
// + time-lapse 6 mois.

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import GIF from "gif.js";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const CI_BOUNDS = [[-8.65, 4.30], [-2.45, 10.80]];

const LEVEL_COLORS = {
  cold:   "#1a1a1a",
  warm:   "#3d2817",
  medium: "#d97706",
  hot:    "#fbbf24",
};

const LEVEL_LABELS = {
  cold:   "❄️ Froid (0 adresse)",
  warm:   "🟠 Tiède (1-9)",
  medium: "🔥 Moyen (10-99)",
  hot:    "✨ Chaud (100+)",
};

function KpiCard({ icon, label, value, sublabel, accent }) {
  return (
    <div
      data-testid={`coverage-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        flex: 1,
        minWidth: 180,
        padding: "14px 16px",
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${accent}55`,
        borderRadius: 10,
        display: "flex", flexDirection: "column", gap: 4,
      }}
    >
      <div style={{ fontSize: 11, color: "#94A3B8", letterSpacing: 1, textTransform: "uppercase" }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent }}>{value}</div>
      {sublabel && <div style={{ fontSize: 11, color: "#64748B" }}>{sublabel}</div>}
    </div>
  );
}

function DepartmentPanel({ feature, onClose }) {
  if (!feature) return null;
  const p = feature.properties || {};
  return (
    <div
      data-testid="coverage-detail-panel"
      style={{
        position: "absolute", top: 12, right: 12, width: 280,
        padding: "14px 16px",
        background: "rgba(15,23,42,0.95)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        backdropFilter: "blur(8px)",
        zIndex: 5,
        color: "#F1F5F9",
        fontSize: 13,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 8 }}>
        <strong style={{ fontSize: 15 }}>📍 {p.souspref_name || p.NAME_3 || "—"}</strong>
        <button
          onClick={onClose}
          data-testid="coverage-panel-close"
          style={{ background: "transparent", border: 0, color: "#94A3B8", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
          aria-label="Fermer"
        >×</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
        <div><span style={{ color: "#64748B" }}>District :</span> {p.district_name || "—"}</div>
        <div><span style={{ color: "#64748B" }}>Région :</span> {p.region_name || "—"}</div>
        <div style={{
          marginTop: 4, padding: "8px 10px",
          background: `${LEVEL_COLORS[p.illumination_level] || "#1a1a1a"}33`,
          border: `1px solid ${LEVEL_COLORS[p.illumination_level] || "#404040"}88`,
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: LEVEL_COLORS[p.illumination_level] || "#94A3B8" }}>
            {p.claimed || 0}
          </div>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>adresses revendiquées</div>
          <div style={{ fontSize: 11, color: "#CBD5E1", marginTop: 4 }}>
            {LEVEL_LABELS[p.illumination_level] || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CoverageMapTab() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [geojson, setGeojson] = useState(null);
  const [lights, setLights] = useState(null);
  const [ciOutline, setCiOutline] = useState(null);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // v2.19.26 — Time-lapse slider
  const SIX_MONTHS_MS = 180 * 24 * 3600 * 1000;
  const TIMELAPSE_START = useMemo(() => Date.now() - SIX_MONTHS_MS, []);
  const TIMELAPSE_END = useMemo(() => Date.now(), []);
  const [asOfTs, setAsOfTs] = useState(TIMELAPSE_END); // null = live, sinon timestamp
  const [playing, setPlaying] = useState(false);
  const playRafRef = useRef(null);
  const timelapseStatsRef = useRef(null);

  // ─── Fetch stats + geojson en parallèle (avec optional asOf) ───
  const fetchAll = async (asOfIso = null) => {
    setRefreshing(true);
    try {
      const qs = asOfIso ? `?as_of=${encodeURIComponent(asOfIso)}` : "";
      const [statsRes, geoRes, lightsRes] = await Promise.all([
        fetch(`${API}/api/v3/addresses/coverage-stats${qs}`).then((r) => r.json()),
        fetch(`${API}/api/v3/addresses/coverage-geojson${qs}`).then((r) => r.json()),
        fetch(`${API}/api/public/claims-lights`).then((r) => r.json()).catch(() => null),
      ]);
      setStats(statsRes);
      setGeojson(geoRes);
      if (lightsRes) setLights(lightsRes);
      timelapseStatsRef.current = statsRes;
    } catch (e) {
      toast.error("Erreur de chargement des données carte");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // v2.19.37c — fetch CI outline (one-shot, contour gold silhouette)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/ci-outline`);
        setCiOutline(await r.json());
      } catch (_) {}
    })();
  }, []);

  // ─── Time-lapse playback ───
  useEffect(() => {
    if (!playing) return;
    const FRAME_MS = 280;
    const STEPS = 42; // ~12s pour parcourir 6 mois
    const stepSize = (TIMELAPSE_END - TIMELAPSE_START) / STEPS;
    let current = asOfTs;
    if (current >= TIMELAPSE_END) current = TIMELAPSE_START; // restart
    const tick = async () => {
      if (current > TIMELAPSE_END) { setPlaying(false); return; }
      setAsOfTs(current);
      await fetchAll(new Date(current).toISOString());
      current += stepSize;
      playRafRef.current = setTimeout(tick, FRAME_MS);
    };
    tick();
    return () => { if (playRafRef.current) clearTimeout(playRafRef.current); };
  }, [playing]);

  const onSliderChange = (e) => {
    const ts = parseInt(e.target.value, 10);
    setAsOfTs(ts);
    fetchAll(new Date(ts).toISOString());
  };

  // ─── v2.19.26-bis — Export GIF / MP4 du time-lapse ────────────────────────
  const [recording, setRecording] = useState(null); // { type, frame, total } | null

  const exportTimelapse = useCallback(async (mode /* "gif" | "mp4" */) => {
    if (recording || playing) return;
    const map = mapRef.current;
    if (!map) { toast.error("Carte non prête"); return; }

    const STEPS = 42;
    const FRAME_MS = 280;
    const stepSize = (TIMELAPSE_END - TIMELAPSE_START) / STEPS;

    // ─── Setup encoder ─────────────────────────────────────────────────────
    let gif = null;
    let recorder = null;
    let chunks = [];
    let mimeType = null;
    const canvas = map.getCanvas();

    if (mode === "gif") {
      gif = new GIF({
        workers: 2,
        quality: 12,
        width: canvas.width,
        height: canvas.height,
        workerScript: "/gif.worker.js",
      });
    } else {
      // MP4 / WebM via MediaRecorder natif. Sur la plupart des browsers seul
      // WebM est natif ; on essaie MP4 en priorité (Safari), fallback WebM.
      const candidates = [
        "video/mp4;codecs=avc1",
        "video/mp4",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
      ];
      mimeType = candidates.find((t) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || "video/webm";
      const stream = canvas.captureStream(8); // 8 fps suffit pour ~12s
      recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      recorder.start();
    }

    setRecording({ type: mode, frame: 0, total: STEPS });
    setPlaying(false);

    try {
      for (let i = 0; i <= STEPS; i++) {
        const ts = TIMELAPSE_START + i * stepSize;
        setAsOfTs(ts);
        await fetchAll(new Date(ts).toISOString());
        // Attend le repaint complet (style + transitions Mapbox).
        await new Promise((r) => setTimeout(r, FRAME_MS));
        // Force render avant capture
        map.triggerRepaint();
        await new Promise((r) => requestAnimationFrame(() => r()));
        if (gif) {
          gif.addFrame(canvas, { delay: FRAME_MS, copy: true });
        }
        setRecording({ type: mode, frame: i + 1, total: STEPS });
      }
    } catch (e) {
      console.error("[exportTimelapse]", e);
      toast.error("Erreur capture timeline");
      setRecording(null);
      if (recorder) try { recorder.stop(); } catch (_) {}
      return;
    }

    // ─── Finalize + download ────────────────────────────────────────────────
    if (gif) {
      gif.on("finished", (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aya-coverage-${new Date().toISOString().slice(0, 10)}.gif`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`GIF exporté (${(blob.size / 1024 / 1024).toFixed(2)} Mo)`);
        setRecording(null);
      });
      gif.on("abort", () => setRecording(null));
      gif.render();
    } else if (recorder) {
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";
        const a = document.createElement("a");
        a.href = url;
        a.download = `aya-coverage-${new Date().toISOString().slice(0, 10)}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Vidéo ${ext.toUpperCase()} exportée (${(blob.size / 1024 / 1024).toFixed(2)} Mo)`);
        setRecording(null);
      };
      recorder.stop();
    }
  }, [recording, playing]);

  // ─── Init Mapbox ───
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !MAPBOX_TOKEN) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      bounds: CI_BOUNDS,
      fitBoundsOptions: { padding: 40 },
      preserveDrawingBuffer: true, // permet getCanvas().toDataURL()
    });
    mapRef.current = map;

    map.on("load", () => {
      // v2.19.37c — Style "Night Lights" : silhouette CI gold + heatmap doré + points individuels.
      // CI outline via gadm L1
      map.addSource("ci-outline", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "ci-border-glow-wide", type: "line", source: "ci-outline",
        paint: { "line-color": "#FFD700", "line-width": 5, "line-opacity": 0.18, "line-blur": 8 },
      });
      map.addLayer({
        id: "ci-border-glow", type: "line", source: "ci-outline",
        paint: { "line-color": "#FFD700", "line-width": 1.2, "line-opacity": 0.55, "line-blur": 2 },
      });

      // Souspref grid subtil + fill transparent (gardé pour mousemove/click handlers existants)
      map.addSource("coverage", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "coverage-fill",
        type: "fill",
        source: "coverage",
        paint: { "fill-color": "#000000", "fill-opacity": 0 },
      });
      map.addLayer({
        id: "coverage-outline",
        type: "line",
        source: "coverage",
        paint: { "line-color": "#2A3B5C", "line-width": 0.3, "line-opacity": 0.2 },
      });

      // Night-lights heatmap
      map.addSource("claims-lights", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "lights-heatmap",
        type: "heatmap",
        source: "claims-lights",
        maxzoom: 14,
        paint: {
          "heatmap-weight": ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 0, 0.4, 10, 1.2],
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 5, 1.0, 12, 2.5],
          "heatmap-radius":    ["interpolate", ["linear"], ["zoom"], 5, 14, 10, 30, 14, 60],
          "heatmap-opacity":   ["interpolate", ["linear"], ["zoom"], 5, 0.85, 14, 0.55],
          "heatmap-color": [
            "interpolate", ["linear"], ["heatmap-density"],
            0,    "rgba(0,0,0,0)",
            0.15, "rgba(255,176,0,0.35)",
            0.35, "rgba(255,200,40,0.6)",
            0.6,  "rgba(255,224,102,0.85)",
            0.85, "rgba(255,245,200,0.95)",
            1,    "rgba(255,255,255,1)",
          ],
        },
      });

      // Night-lights circles individuels
      map.addLayer({
        id: "lights-circles",
        type: "circle",
        source: "claims-lights",
        minzoom: 6,
        paint: {
          "circle-color": "#FFE066",
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            6,  ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 1.5, 10, 3],
            10, ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 3,   10, 6],
            14, ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 5,   10, 10],
          ],
          "circle-blur": 0.6,
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.5, 10, 0.85, 14, 1.0],
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 0.4,
          "circle-stroke-opacity": 0.6,
        },
      });

      // Hover highlight (gardé pour interactivité OPS)
      map.addLayer({
        id: "coverage-hover",
        type: "line",
        source: "coverage",
        paint: { "line-color": "#FFE066", "line-width": 2.5 },
        filter: ["==", "GID_3", ""],
      });
    });

    map.on("mousemove", "coverage-fill", (e) => {
      if (!e.features || e.features.length === 0) return;
      map.getCanvas().style.cursor = "pointer";
      const f = e.features[0];
      map.setFilter("coverage-hover", ["==", "GID_3", f.properties.GID_3]);
      const p = f.properties;
      if (!popupRef.current) {
        popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
      }
      const tooltip = `
        <div style="font-family:system-ui;font-size:12px;color:#F1F5F9;background:rgba(15,23,42,0.95);padding:8px 10px;border-radius:8px;border:1px solid rgba(255,255,255,0.1);">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px;">📍 ${p.souspref_name || p.NAME_3 || "—"}</div>
          <div style="color:#94A3B8;">District : <span style="color:#CBD5E1;">${p.district_name || "—"}</span></div>
          <div style="color:#94A3B8;">Région : <span style="color:#CBD5E1;">${p.region_name || "—"}</span></div>
          <div style="margin-top:4px;color:#fbbf24;font-weight:600;">Adresses : ${p.claimed || 0}</div>
          <div style="font-size:11px;color:#94A3B8;">${LEVEL_LABELS[p.illumination_level] || ""}</div>
        </div>
      `;
      popupRef.current.setLngLat(e.lngLat).setHTML(tooltip).addTo(map);
    });
    map.on("mouseleave", "coverage-fill", () => {
      map.getCanvas().style.cursor = "";
      map.setFilter("coverage-hover", ["==", "GID_3", ""]);
      if (popupRef.current) popupRef.current.remove();
    });
    map.on("click", "coverage-fill", (e) => {
      if (!e.features || e.features.length === 0) return;
      const f = e.features[0];
      setSelected(f);
      // Zoom on bbox
      try {
        const g = f.geometry;
        const polys = g.type === "MultiPolygon" ? g.coordinates : [g.coordinates];
        let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
        for (const poly of polys) for (const [lng, lat] of poly[0]) {
          if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
          if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
        }
        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, duration: 800 });
      } catch (_) {}
    });

    return () => { try { map.remove(); } catch (_) {} mapRef.current = null; };
  }, []);

  // ─── Push geojson into map ───
  useEffect(() => {
    if (!geojson || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("coverage");
      if (src) src.setData(geojson);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [geojson]);

  // v2.19.37c — Push night-lights vers la map
  useEffect(() => {
    if (!lights || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("claims-lights");
      if (src) src.setData(lights);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [lights]);

  // v2.19.37c — Push CI outline (gold silhouette) vers la map
  useEffect(() => {
    if (!ciOutline || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("ci-outline");
      if (src) src.setData(ciOutline);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [ciOutline]);

  // ─── KPI calc ───
  const kpis = useMemo(() => {
    if (!stats) return null;
    const total = stats.global.total_claimed;
    const lit_souspref = (stats.by_souspref || []).filter((s) => s.claimed > 0).length;
    const lit_regions  = (stats.by_region || []).filter((r) => r.claimed > 0).length;
    const lit_districts = (stats.by_district || []).filter((d) => d.claimed > 0).length;
    return {
      total,
      sousprefRatio: `${lit_souspref}/${stats.global.total_addressable_units} (${stats.global.illumination_ratio_pct}%)`,
      regionsRatio:  `${lit_regions}/${stats.global.total_regions} (${(lit_regions * 100 / stats.global.total_regions).toFixed(1)}%)`,
      districtsRatio:`${lit_districts}/${stats.global.total_districts} (${(lit_districts * 100 / stats.global.total_districts).toFixed(1)}%)`,
    };
  }, [stats]);

  const exportPng = () => {
    if (!mapRef.current) return;
    try {
      const canvas = mapRef.current.getCanvas();
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `carte-couverture-civ-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      toast.success("Capture exportée");
    } catch (e) {
      toast.error("Export PNG impossible (CORS Mapbox)");
    }
  };

  const zoomOut = () => {
    setSelected(null);
    if (mapRef.current) mapRef.current.fitBounds(CI_BOUNDS, { padding: 40, duration: 800 });
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{ padding: 24, color: "#FCD34D" }}>
        ⚠️ Token Mapbox manquant (REACT_APP_MAPBOX_TOKEN).
      </div>
    );
  }

  return (
    <div data-testid="coverage-map-tab" style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
      <div>
        <h2 style={{ margin: "0 0 4px", fontSize: 20, color: "#F1F5F9" }}>
          🗺️ Carte de Couverture Nationale CI
        </h2>
        <p style={{ margin: 0, fontSize: 12, color: "#94A3B8" }}>
          474 sous-préfectures (OSM L7/L8 + GADM) — illumination par adresses revendiquées.
        </p>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <KpiCard icon="📍" label="Total adresses" value={kpis ? kpis.total : "—"} accent="#fbbf24" />
        <KpiCard icon="🏘" label="Départements" value={kpis ? kpis.sousprefRatio : "—"} sublabel="illuminés / total" accent="#d97706" />
        <KpiCard icon="🗺" label="Régions" value={kpis ? kpis.regionsRatio : "—"} sublabel="couvertes / total" accent="#60A5FA" />
        <KpiCard icon="🏛" label="Districts" value={kpis ? kpis.districtsRatio : "—"} sublabel="couverts / total" accent="#A78BFA" />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={fetchAll}
          disabled={refreshing}
          data-testid="coverage-refresh-btn"
          className="opsb2b-btn-ghost"
          style={{ padding: "6px 12px", fontSize: 12 }}
        >🔄 {refreshing ? "..." : "Rafraîchir"}</button>
        <button
          onClick={exportPng}
          data-testid="coverage-export-btn"
          className="opsb2b-btn-ghost"
          style={{ padding: "6px 12px", fontSize: 12 }}
        >📸 Capturer la carte</button>
        {selected && (
          <button
            onClick={zoomOut}
            data-testid="coverage-zoomout-btn"
            className="opsb2b-btn-ghost"
            style={{ padding: "6px 12px", fontSize: 12 }}
          >🔍 Vue globale</button>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, fontSize: 11, color: "#94A3B8" }}>
          {Object.entries(LEVEL_LABELS).map(([k, lbl]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ display: "inline-block", width: 12, height: 12, background: LEVEL_COLORS[k], borderRadius: 3 }} />
              {lbl}
            </div>
          ))}
        </div>
      </div>

      <div
        ref={mapContainerRef}
        data-testid="coverage-map-container"
        style={{
          flex: 1,
          minHeight: 480,
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          position: "relative",
        }}
      >
        <DepartmentPanel feature={selected} onClose={() => setSelected(null)} />
      </div>

      {/* v2.19.26 — Time-lapse slider */}
      <div data-testid="coverage-timelapse" style={{
        padding: "10px 14px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 10,
        display: "flex", flexDirection: "column", gap: 6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#CBD5E1", flexWrap: "wrap" }}>
          <button
            onClick={() => setPlaying((v) => !v)}
            disabled={!!recording}
            data-testid="timelapse-play-btn"
            className="opsb2b-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 12, minWidth: 90, opacity: recording ? 0.4 : 1 }}
          >{playing ? "⏸ Pause" : "▶ Lecture"}</button>
          <span style={{ color: "#fbbf24", fontWeight: 600, minWidth: 220 }}>
            📅 Couverture au {new Date(asOfTs).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
          </span>
          <span style={{ color: "#94A3B8", fontSize: 11 }}>
            {stats ? `${stats.global.total_claimed} adresses · ${(stats.by_souspref || []).filter((s) => s.claimed > 0).length}/${stats.global.total_addressable_units} souspref illuminées` : "…"}
          </span>
          <button
            onClick={() => exportTimelapse("gif")}
            disabled={!!recording || playing}
            data-testid="timelapse-export-gif"
            className="opsb2b-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11, opacity: (recording || playing) ? 0.4 : 1 }}
          >🎞️ Export GIF</button>
          <button
            onClick={() => exportTimelapse("mp4")}
            disabled={!!recording || playing}
            data-testid="timelapse-export-mp4"
            className="opsb2b-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11, opacity: (recording || playing) ? 0.4 : 1 }}
          >🎬 Export MP4</button>
          <button
            onClick={() => { setAsOfTs(TIMELAPSE_END); setPlaying(false); fetchAll(); }}
            disabled={!!recording}
            data-testid="timelapse-now-btn"
            className="opsb2b-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11, marginLeft: "auto", opacity: recording ? 0.4 : 1 }}
          >⏭ Aujourd'hui</button>
        </div>
        {recording && (
          <div data-testid="timelapse-recording-overlay" style={{
            marginTop: 6,
            padding: "8px 12px",
            background: "rgba(251,191,36,0.15)",
            border: "1px solid rgba(251,191,36,0.5)",
            borderRadius: 8,
            color: "#FDE68A",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}>
            <span style={{ fontWeight: 700 }}>
              {recording.type === "gif" ? "🎞️ Encodage GIF…" : "🎬 Enregistrement MP4…"}
            </span>
            <span>Frame {recording.frame}/{recording.total}</span>
            <div style={{ flex: 1, height: 6, background: "rgba(0,0,0,0.3)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                width: `${(recording.frame / recording.total) * 100}%`,
                height: "100%",
                background: "linear-gradient(90deg, #fbbf24, #d97706)",
                transition: "width 200ms ease",
              }} />
            </div>
          </div>
        )}
        <input
          type="range"
          min={TIMELAPSE_START}
          max={TIMELAPSE_END}
          value={asOfTs}
          onChange={onSliderChange}
          disabled={!!recording}
          data-testid="timelapse-slider"
          style={{ width: "100%", accentColor: "#fbbf24", opacity: recording ? 0.4 : 1 }}
        />
      </div>
    </div>
  );
}
